import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal as AntModal,
  Popconfirm,
  Card,
  Statistic,
  Row,
  Col,
  App,
} from "antd";
import {
  FileExcelOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import dayjs from "dayjs";
import apiService from "../../services/api";
import { formatDateTime } from "../../utils/formatters";
import { useAuth } from "../../hooks/useAuth";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import "./Reports.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

// 报告状态标签配置
const statusConfig = {
  pending: { color: "default", text: "待生成" },
  generating: { color: "processing", text: "生成中" },
  completed: { color: "success", text: "已完成" },
  failed: { color: "error", text: "生成失败" },
};

// 报告类型配置
const reportTypeConfig = {
  daily: "日报",
  weekly: "周报",
  monthly: "月报",
  custom: "自定义",
};

const Reports = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canGenerateReport = hasPermission(user, PERMISSIONS.GENERATE_REPORTS);
  const canDeleteReport = (record) =>
    isAdmin || Number(record?.user_id) === Number(user?.id);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { message: msg } = App.useApp();
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const navigate = useNavigate();

  // 监听表单变化，自动填充日期和名称
  const reportType = Form.useWatch("type", form);
  const presetKey = Form.useWatch("preset", form);

  // 预设选项定义
  const presets = useMemo(() => ({
    daily: [
      { label: "今天", value: "today" },
      { label: "昨天", value: "yesterday" },
      { label: "前天", value: "before_yesterday" },
    ],
    weekly: [
      { label: "本周 (周一至今)", value: "this_week" },
      { label: "上周 (周一至周日)", value: "last_week" },
      { label: "前周", value: "two_weeks_ago" },
    ],
    monthly: [
      { label: "本月 (1号至今)", value: "this_month" },
      { label: "上月", value: "last_month" },
      { label: "前月", value: "two_months_ago" },
    ],
  }), []);

  // 根据类型和预设计算日期范围
  useEffect(() => {
    if (!reportType || reportType === 'custom' || !presetKey) return;

    let start, end;
    const now = dayjs();

    if (reportType === 'daily') {
      if (presetKey === 'today') { start = now.startOf('day'); end = now.endOf('day'); }
      else if (presetKey === 'yesterday') { start = now.subtract(1, 'day').startOf('day'); end = now.subtract(1, 'day').endOf('day'); }
      else if (presetKey === 'before_yesterday') { start = now.subtract(2, 'day').startOf('day'); end = now.subtract(2, 'day').endOf('day'); }
    } else if (reportType === 'weekly') {
      if (presetKey === 'this_week') { start = now.startOf('week').add(1, 'day'); end = now; } // Monday start
      else if (presetKey === 'last_week') { start = now.subtract(1, 'week').startOf('week').add(1, 'day'); end = now.subtract(1, 'week').endOf('week').add(1, 'day'); }
      else if (presetKey === 'two_weeks_ago') { start = now.subtract(2, 'week').startOf('week').add(1, 'day'); end = now.subtract(2, 'week').endOf('week').add(1, 'day'); }
    } else if (reportType === 'monthly') {
      if (presetKey === 'this_month') { start = now.startOf('month'); end = now; }
      else if (presetKey === 'last_month') { start = now.subtract(1, 'month').startOf('month'); end = now.subtract(1, 'month').endOf('month'); }
      else if (presetKey === 'two_months_ago') { start = now.subtract(2, 'month').startOf('month'); end = now.subtract(2, 'month').endOf('month'); }
    }

    if (start && end) {
      form.setFieldsValue({ 
        dateRange: [start, end],
        name: `${reportTypeConfig[reportType]}_${start.format('YYYYMMDD')}${reportType !== 'daily' ? '_'+end.format('YYYYMMDD') : ''}`
      });
    }
  }, [reportType, presetKey, form]);

  // 修改类型时重置预设
  const onTypeChange = () => {
    form.setFieldsValue({ preset: undefined, dateRange: undefined });
  };

  // 获取报告列表
  const fetchReports = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const response = await apiService.reports.getReports({
        page,
        page_size: pageSize,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "获取报告列表失败");
      }

      setReports(response.data.data?.list || []);
      setPagination({
        current: page,
        pageSize,
        total: response.data.data?.total || 0,
      });
    } catch (error) {
      console.error("获取报告列表失败:", error);
      setReports([]);
      msg.error(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "获取报告列表失败",
      );
    } finally {
      setLoading(false);
    }
  };

  // 生成报告
  const handleGenerateReport = async (values) => {
    try {
      const reportData = {
        name: values.name,
        description: values.description || "",
        type: values.type,
        start_date: values.dateRange[0].format("YYYY-MM-DD"),
        end_date: values.dateRange[1].format("YYYY-MM-DD"),
      };

      const response = await apiService.reports.generateReport(reportData);

      if (!response.data.success) {
        throw new Error(response.data.message || "生成报告失败");
      }

      msg.success("报告生成任务已启动");
      setIsModalVisible(false);
      form.resetFields();
      fetchReports(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("生成报告失败:", error);
      msg.error(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "生成报告失败",
      );
    }
  };

  // 删除报告
  const handleDelete = async (reportId) => {
    try {
      const response = await apiService.reports.deleteReport(reportId);

      if (!response.data.success) {
        throw new Error(response.data.message || "删除报告失败");
      }

      msg.success("删除报告成功");
      fetchReports(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("删除报告失败:", error);
      msg.error(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "删除报告失败",
      );
    }
  };

  // 查看报告详情
  const handleViewDetail = (reportId) => {
    navigate(`/reports/${reportId}`);
  };

  // 表格列配置
  const columns = [
    {
      title: "报告名称",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => reportTypeConfig[type] || type,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={statusConfig[status]?.color || "default"}>
          {statusConfig[status]?.text || status}
        </Tag>
      ),
    },
    {
      title: "时间范围",
      key: "dateRange",
      width: 220,
      render: (_, record) => `${record.start_date} ~ ${record.end_date}`,
    },
    {
      title: "创建者",
      dataIndex: "user_name",
      key: "user_name",
      width: 120,
    },
    {
      title: "文件大小",
      dataIndex: "file_size",
      key: "file_size",
      width: 100,
      render: (size) => (size ? `${(size / 1024).toFixed(2)} KB` : "-"),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date) => formatDateTime(date),
    },
    {
      title: "操作",
      key: "action",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          >
            详情
          </Button>

          {canDeleteReport(record) && (
            <Popconfirm
              title="确定要删除这个报告吗?"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // 表格分页配置
  const handleTableChange = (paginationConfig) => {
    fetchReports(paginationConfig.current, paginationConfig.pageSize);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  return (
    <div className="reports-container">
      {/* 页面标题和操作按钮 */}
      <Row justify="space-between" align="middle" className="page-header">
        <Col className="header-content">
          <h2 className="page-title">报告管理</h2>
          <p className="page-subtitle">生成和管理交易量综合报告</p>
        </Col>
        <Col className="header-actions">
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchReports(pagination.current, pagination.pageSize)
              }
            >
              刷新
            </Button>
            {canGenerateReport && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                   form.resetFields();
                   setIsModalVisible(true);
                }}
              >
                生成报告
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* 统计信息 */}
      <Row gutter={16} className="stats-row">
        <Col span={6}>
          <Card className="stat-card stat-card-total">
            <div className="stat-header">
              <div className="stat-icon total">
                <FileExcelOutlined />
              </div>
              <div className="stat-label">总报告数</div>
            </div>
            <div className="stat-value" style={{ color: "var(--color-primary)" }}>
              {pagination.total} <span className="stat-unit">份</span>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card stat-card-completed">
            <div className="stat-header">
              <div className="stat-icon completed">
                <CheckCircleOutlined />
              </div>
              <div className="stat-label">已完成</div>
            </div>
            <div className="stat-value" style={{ color: "#52c41a" }}>
              {(reports || []).filter((r) => r.status === "completed").length} <span className="stat-unit">份</span>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card stat-card-generating">
            <div className="stat-header">
              <div className="stat-icon generating">
                <SyncOutlined spin />
              </div>
              <div className="stat-label">生成中</div>
            </div>
            <div className="stat-value" style={{ color: "#1890ff" }}>
              {(reports || []).filter((r) => r.status === "generating").length} <span className="stat-unit">份</span>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card stat-card-failed">
            <div className="stat-header">
              <div className="stat-icon failed">
                <CloseCircleOutlined />
              </div>
              <div className="stat-label">失败</div>
            </div>
            <div className="stat-value" style={{ color: "#ff4d4f" }}>
              {(reports || []).filter((r) => r.status === "failed").length} <span className="stat-unit">份</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 报告列表表格 */}
      <Card className="reports-list-card">
        <Table
          className="reports-table"
          columns={columns}
          dataSource={reports || []}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 生成报告对话框 */}
      <Modal
        className="reports-modal"
        title="生成报告"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onOk={() => {
          form.validateFields().then((values) => {
            handleGenerateReport(values);
          });
        }}
        okText="生成"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          className="reports-form"
          initialValues={{ type: "daily" }}
        >
          <Form.Item
            label="报告类型"
            name="type"
            rules={[{ required: true, message: "请选择报告类型" }]}
          >
            <Select placeholder="请选择报告类型" onChange={onTypeChange}>
              <Option value="daily">日报</Option>
              <Option value="weekly">周报</Option>
              <Option value="monthly">月报</Option>
              <Option value="custom">自定义</Option>
            </Select>
          </Form.Item>

          {reportType && reportType !== 'custom' && (
            <Form.Item
              label="快捷选择"
              name="preset"
              rules={[{ required: true, message: "请选择预设范围" }]}
            >
              <Select placeholder="选择一个预设时间范围">
                {(presets[reportType] || []).map(p => (
                   <Option key={p.value} value={p.value}>{p.label}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="时间范围"
            name="dateRange"
            rules={[{ required: true, message: "请选择时间范围" }]}
          >
            <RangePicker
              style={{ width: "100%" }}
              placeholder={["开始日期", "结束日期"]}
            />
          </Form.Item>

          <Form.Item
            label="报告名称"
            name="name"
            rules={[{ required: true, message: "请输入报告名称" }]}
          >
            <Input placeholder="请输入报告名称" />
          </Form.Item>

          <Form.Item label="报告描述" name="description">
            <Input.TextArea placeholder="请输入报告描述（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Reports;
